import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  AuthAlert,
  AuthField,
  AuthInput,
  AuthPageLayout,
  AuthSubmitButton,
  authStyles,
} from '../../auth/components/AuthPageLayout';
import { globalSignUp, savePickerSession } from '../services/workspaceService';
import styles from './WorkspacePicker.module.css';

const MINIMUM_PASSWORD_LENGTH = 8;

/**
 * Creates the Simaizeer account on the global app host. There is deliberately no workspace yet: the
 * new account goes on to create its first one, or to be invited into somebody else's.
 */
export const SignUpPage: React.FC = () => {
  const { t } = useTranslation(['workspaces', 'auth']);
  const navigate = useNavigate();
  const [displayName, setDisplayName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t('auth:errors.usernameRequired'));
      return;
    }

    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      setError(t('workspaces:signUp.passwordTooShort', { count: MINIMUM_PASSWORD_LENGTH }));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await globalSignUp(email.trim(), password, displayName.trim());

      // A brand new account has no workspaces, so the picker would be an empty page.
      savePickerSession({
        token: '',
        expiresAt: result.accountExpiresAtUtc,
        username: result.account.email,
        workspaces: [],
        accountToken: result.accountToken,
      });

      navigate('/workspaces/new', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('workspaces:errors.signUpFailed'));
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageLayout title={t('workspaces:signUp.title')} subtitle={t('workspaces:signUp.subtitle')}>
      <form className={authStyles.form} onSubmit={onSubmit}>
        {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}

        <AuthField label={t('workspaces:signUp.nameLabel')} htmlFor="signup-name" icon="mail">
          <AuthInput
            id="signup-name"
            value={displayName}
            onChange={event => setDisplayName(event.target.value)}
            autoComplete="name"
            placeholder={t('workspaces:signUp.namePlaceholder')}
          />
        </AuthField>

        <AuthField label={t('auth:emailLabel')} htmlFor="signup-email" icon="mail">
          <AuthInput
            id="signup-email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            autoComplete="username"
            placeholder={t('auth:emailPlaceholder')}
          />
        </AuthField>

        <AuthField label={t('auth:passwordLabel')} htmlFor="signup-password" icon="lock">
          <AuthInput
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={event => setPassword(event.target.value)}
            autoComplete="new-password"
            placeholder={t('auth:passwordPlaceholder')}
            withToggle
            revealed={showPassword}
            onToggleReveal={() => setShowPassword(current => !current)}
            toggleLabel={showPassword ? t('auth:hidePassword') : t('auth:showPassword')}
          />
        </AuthField>

        <AuthSubmitButton loading={isSubmitting} loadingLabel={t('workspaces:signUp.submitting')}>
          {t('workspaces:signUp.submit')}
        </AuthSubmitButton>
      </form>

      <p className={styles.footer}>
        <span>{t('workspaces:signUp.haveAccount')}</span>
        <Link className={styles.linkButton} to="/login">
          {t('auth:signIn')}
        </Link>
      </p>
    </AuthPageLayout>
  );
};

export default SignUpPage;
